/**
 * Design tokens for the pricing page that have no equivalent in `assets/theme.ts`.
 *
 * The palette comes from the "more professional" redesign pass (header variant
 * 2c, plan/comparison content from 1a) and is distinct from the app's usual
 * orange/beige marketing look — a warmer, editorial cream-and-navy system with
 * Newsreader serif italics for captions and attributions. `theme.colors.orange`
 * / `orangeDark` / `paleBeige` are reused directly below where the mock's hex
 * values happen to match them exactly; everything else here is new.
 */

/** Primary heading/body text — this design's navy, distinct from theme.colors.black. */
export const NAVY = '#1E2233';

/** Page canvas background behind the header and section wrappers. */
export const CREAM_BG = '#F6EFE8';

/** Card/panel background (the header's outer shell, plan cards). */
export const CARD_CREAM = '#FDF3ED';

/** Standard card/panel border. */
export const BORDER_WARM = '#E7DDD2';

/** Lighter row divider inside comparison-table cards. */
export const HAIRLINE_WARM = '#F1EAE1';

/** Uppercase eyebrow labels ("PRICING", "PLANS", "COMPLIANCE"). */
export const MUTED_LABEL = '#8A8578';

/** Paragraph / subtitle body copy. */
export const MUTED_BODY = '#4C505F';

/** Italic Newsreader captions, attributions and descriptions. */
export const MUTED_ITALIC = '#5A5F70';

/** Slightly bolder secondary text — comparison-table values, evidence-row copy. */
export const TEXT_STRONG = '#3A3F52';

/** Underline accent on inline text links ("See all →", "Full comparison →"). */
export const LINK_UNDERLINE = '#F0C79B';

/** Trend-up icon stroke. */
export const ACCENT_GREEN = '#2F8F63';

/** School & District's highlighted column in the comparison table. */
export const HIGHLIGHT_BG = '#FEF6EC';
export const HIGHLIGHT_BORDER = '#F3DCC2';
export const HIGHLIGHT_TEXT = '#C6752B';

/** School & District plan card's emphasis border + "Most chosen" badge. */
export const EMPHASIS_BORDER = '#F0A868';
export const BADGE_BG = '#FDECDB';

/** Plan-card top accent strips. */
export const STRIP_FREE = '#EFE6DA';
export const STRIP_CURRICULUM = '#CBA8CB';

/** Outline-button border + hover background (Free and Curriculum Alignment CTAs). */
export const OUTLINE_BORDER = '#D8CDBF';
export const OUTLINE_HOVER_BG = '#FEF4EA';

/** Serif face used for italic captions throughout this design. */
export const SERIF_FONT = "'Newsreader', Georgia, serif";

/** Primary breakpoint: below this the multi-column grids become stacked cards. */
export const MOBILE_QUERY = '(max-width: 992px)';

/** Between MOBILE_QUERY and this, five columns need horizontal scrolling. */
export const TABLET_QUERY = '(max-width: 1200px)';

/** Small phones — tighter padding and type. */
export const NARROW_QUERY = '(max-width: 600px)';

/** Shared content width, matching the mock's 1240px container. */
export const CONTENT_MAX_WIDTH = '1240px';

/**
 * `LandingPageHeader`'s own nav bar (`TopBar`/`MobileTopBar`) is
 * `position: fixed; top: 0; height: 60px` with a z-index far above anything
 * on this page — a `position: sticky; top: 0` element on the page itself
 * sticks right underneath it. Anything sticky here needs `top: NAV_BAR_HEIGHT`
 * instead of `top: 0` to clear it.
 */
export const NAV_BAR_HEIGHT = '60px';

/**
 * The pre-redesign plan overview ("old" PlanOverviewSection) uses the app's
 * usual orange/beige system rather than this design's navy/cream palette —
 * these tokens support it and have no equivalent above.
 */
export const HEADER_BEIGE = '#FFE6CC';
export const CARD_SHADOW = '0 2px 20px 0 #deaf8d33';
export const HAIRLINE = '#deaf8d33';
export const MUTED = 'rgba(40,47,51,.6)';
export const MUTED_STRONG = 'rgba(40,47,51,.75)';

/** Header variant 2b's pastel case-study tile grid — three tints plus the
 *  white/bordered treatment for the last tile, matching the mock. */
export const PASTEL_PEACH = '#F9CFAB';
export const PASTEL_TAN = '#F1E7D3';
export const PASTEL_PURPLE = '#D3B4D2';

/** Body copy inside a pastel tile — distinct from TEXT_STRONG's cooler grey. */
export const TILE_TEXT = '#2B2333';

/**
 * Plan-overview matrix (design variant 4c/4e) — a bordered "identity / price /
 * action" grid that reflows into stacked cards on mobile. `theme.colors.
 * landingPageBeige` / `paleBeige` / `orange` are reused below where the mock's
 * hex values match them exactly; these are the ones with no equivalent.
 */
export const MATRIX_CARD_BORDER = '#EFE3D6';
/** Headings, button ink and the CTA pills' text-on-light colour. */
export const MATRIX_INK = '#282F33';
/** Premium's neutral (uncoloured) column — audience label and "Compare plans" eyebrow. */
export const MATRIX_MUTED = '#7A7469';
export const MATRIX_FREE_BG = '#FCE3C8';
/** Free column's audience label and price unit, on its peach background. */
export const MATRIX_FREE_ACCENT = '#8A5F33';
/** The dark-green plan column's background (currently Curriculum Alignment). */
export const MATRIX_GREEN_BG = '#0F7A68';
/** Audience label and price caption for the orange plan column (currently
 *  School & District), on orange. */
export const MATRIX_ORANGE_ACCENT = '#4A3B28';
/** Mobile stacked-card row labels ("For teachers", ...). */
export const MATRIX_ROW_LABEL = '#9A9287';
